import { useCallback, useEffect, useState } from "react";
import { Download, Landmark } from "lucide-react";
import { toast } from "sonner";
import { API, apiFetch } from "@/utils/api";
import { formatCurrency } from "@/utils/format";
import { nextQuarterDeadline, formatDeadline, windowProgress } from "@/utils/fiscalDeadline";

export default function MobileTaxes() {
  const [summary, setSummary] = useState(null);
  const [d303, setD303] = useState(null);
  const [d130, setD130] = useState(null);
  const [show130, setShow130] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const plazo = nextQuarterDeadline();
  const win = windowProgress();

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const qs = `year=${plazo.year}&quarter=${plazo.quarter}`;
      const [sumRes, r303, r130] = await Promise.all([
        apiFetch(`${API}/fiscal/summary`),
        apiFetch(`${API}/tax-drafts/modelo-303?${qs}&basis=dates`),
        apiFetch(`${API}/tax-drafts/modelo-130?${qs}`),
      ]);
      if (!sumRes.ok) { setError(true); return; }
      setSummary(await sumRes.json());
      setD303(r303.ok ? await r303.json() : null);
      if (r130.ok) { setD130(await r130.json()); setShow130(true); }
      else setShow130(false); // 403 = sociedad o sin estimación directa
    } catch (e) {
      console.error("MobileTaxes load", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [plazo.year, plazo.quarter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const downloadPdf = async (modelo) => {
    const extra = modelo === "303" ? "&basis=dates" : "";
    try {
      const r = await apiFetch(`${API}/tax-drafts/modelo-${modelo}/pdf?year=${plazo.year}&quarter=${plazo.quarter}${extra}`);
      if (!r.ok) { toast.error("No se pudo generar el PDF"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `borrador_${modelo}_${plazo.year}_T${plazo.quarter}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  const res303 = d303?.casillas?.find((c) => c.num === "71")?.importe;

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;
  if (error) return (
    <div data-testid="taxes-error" className="mt-10 bg-white border border-slate-200 rounded-2xl p-5 text-center">
      <p className="text-sm text-slate-700">No se pudieron cargar los datos. Comprueba la conexión.</p>
      <button onClick={loadAll} className="mt-3 min-h-[44px] px-5 rounded-2xl bg-slate-900 text-white text-sm font-semibold">
        Reintentar
      </button>
    </div>
  );

  return (
    <div data-testid="mobile-taxes" className="pb-6">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Impuestos</h1>
      <p className="mt-0.5 text-[12.5px] text-slate-500">
        {plazo.quarter}T {plazo.year}{win.open ? " · ventana de presentación abierta" : ""}
      </p>

      <div className="mt-3.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
        <p className="text-[11.5px] leading-relaxed text-amber-800">
          <strong>Borrador informativo — no válido para presentación.</strong> Tu gestoría presenta; esto te ayuda a anticipar.
        </p>
      </div>

      <div className="mt-2.5 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
          IVA a apartar · {plazo.quarter}T {plazo.year}
        </p>
        <p data-testid="taxes-iva" className="mt-1 font-mono text-3xl text-amber-700 tabular-nums">
          {formatCurrency(summary?.iva_to_reserve)}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Pendiente de presentar: repercutido en tus ventas menos soportado en tus gastos.
        </p>
      </div>

      {summary?.irpf_to_reserve !== 0 && summary !== null && (
        <div className="mt-2.5 bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">IRPF a apartar</p>
            <p className="mt-0.5 text-[11px] text-slate-500">Retenciones a proveedores pendientes de ingresar (modelo 111)</p>
          </div>
          <p data-testid="taxes-irpf" className="font-mono text-lg font-semibold tabular-nums whitespace-nowrap text-slate-900">
            {formatCurrency(summary?.irpf_to_reserve)}
          </p>
        </div>
      )}

      <div data-testid="taxes-plazo" className="mt-2.5 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Plazo de presentación · {plazo.quarter}T
            </p>
            <p className="mt-1 font-heading text-xl font-bold text-amber-700">
              {plazo.daysLeft > 0 ? `Quedan ${plazo.daysLeft} días` : "Último día"}
            </p>
          </div>
          <p className="font-mono text-xs leading-relaxed text-slate-500 text-right">
            hasta el<br />{formatDeadline(plazo)}
          </p>
        </div>
        {win.open && (
          <div data-testid="taxes-plazo-bar" className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-amber-500" style={{ width: `${win.pct}%` }} />
          </div>
        )}
        <p className="mt-2.5 text-[11px] text-slate-400">
          Calendario trimestral: del 1 al 20 de abril · julio · octubre · enero.
        </p>
      </div>

      {(d303 || show130) && (
        <>
          <h2 className="mt-5 mb-2 font-heading text-[15px] font-bold text-slate-900">Borradores AEAT</h2>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
            {d303 && (
              <button data-testid="taxes-303" onClick={() => downloadPdf("303")}
                className="w-full flex items-center gap-3 px-3.5 py-3 min-h-[44px] text-left">
                <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <Landmark className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-medium text-slate-900">Modelo 303 · IVA</span>
                  <span data-testid="taxes-303-result" className="block text-[11.5px] text-slate-500">
                    {plazo.year}-T{plazo.quarter} · resultado{" "}
                    <span className="font-mono font-semibold text-slate-900">{formatCurrency(res303 ?? d303.resultado)}</span>
                  </span>
                  <span className="block text-[10px] text-amber-700">Borrador informativo — tu gestoría presenta</span>
                </span>
                <Download className="w-4 h-4 shrink-0 text-blue-600" strokeWidth={1.5} />
              </button>
            )}
            {show130 && d130 && (
              <button data-testid="taxes-130" onClick={() => downloadPdf("130")}
                className="w-full flex items-center gap-3 px-3.5 py-3 min-h-[44px] text-left">
                <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <Landmark className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-medium text-slate-900">Modelo 130 · Pago fraccionado IRPF</span>
                  <span data-testid="taxes-130-result" className="block text-[11.5px] text-slate-500">
                    {plazo.year}-T{plazo.quarter} · resultado{" "}
                    <span className="font-mono font-semibold text-slate-900">{formatCurrency(d130.resultado)}</span>{" "}
                    (acumulado del año)
                  </span>
                  <span className="block text-[10px] text-amber-700">Borrador informativo — tu gestoría presenta</span>
                </span>
                <Download className="w-4 h-4 shrink-0 text-blue-600" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </>
      )}
      <p className="mt-2.5 px-1 text-[11px] leading-relaxed text-slate-400">
        Solo consulta: el cierre del trimestre (sellado y export a gestoría) se hace en escritorio. Cada casilla del
        borrador y sus fuentes, en el PDF.
      </p>
    </div>
  );
}
